<template>
  <div @change="validateSection">

    <header>
      <h3>Admission</h3>
    </header>

    <div class="content">
      <div class="form__row--x">
        <label>Case #</label>
        <div class="form__row">
          <input type="text"
                 v-model="caseNum"
                 placeholder="Case #"
                 :style="highlightIfError('caseNum')"
                 :disabled="isClose"/>
        </div>
      </div>
      <div class="form__row--x">
        <label>Date of Admission</label>
        <div class="form__row">
          <smd-date-input v-model="admissionDate"
                          :style="highlightIfError('admissionDate')"
                          :min-date="minDate"
                          :max-date="maxDate"
                          :disabled="isClose"/>
          <div v-if="$v.admissionDate.$error" class="error-messages">
            <span v-if="!$v.admissionDate.notFuture">Cannot be a future date</span>
            <span v-if="!$v.admissionDate.notTooOld">Cannot be older than 2 years</span>
          </div>
          <!--  <input type="text"
                 v-model="room"
                 placeholder="Room"
                 :style="highlightIfError('room')" />-->
        </div>
      </div>

      <div class="form__row--x">
        <label>Age on Admission: {{age}} </label>
      </div>

      <div class="form__row--x">
        <div class="form__row max-AOG">
          <div class="smd-input-label">
            <label>Weight (kg)</label>
            <input type="number" min="0" v-model="weight"
                   @change="updateBMI"
                   :style="highlightIfError('weight')"
                   :disabled="isClose"/>
          </div>
          <div class="smd-input-label">
            <label>Height (cm)</label>
            <input type="number" min="0" v-model="height"
                   @change="updateBMI"
                   :style="highlightIfError('height')"
                   :disabled="isClose"/>
          </div>
          <div class="smd-input-label">
            <label>BMI (kg/m<sup>2</sup>)</label>
            <input type="number" min="0" v-model="bmi"
                   :style="highlightIfError('bmi')"
                   disabled="true"/>
          </div>
        </div>
      </div>

      <div class="form__row--x" :style="highlightIfError('caseFormType')">
        <label>Case Type</label>
        <smd-segment type="pill"
                     v-model="caseFormType"
                     :items="$options.caseTypeChoices"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="isPregnancy">
        <label>Admission AOG</label>
        <div class="form__row max-AOG" :style="highlightIfError('admissionAOGType')">
          <smd-segment type="pill"
                       v-model="admissionAOGType"
                       :items="$options.aogTypes()"
                       :disabled="isClose"/>
        </div>
        <div class="form__row max-AOG" v-if="showExactAOG">
          <div class="smd-input-label">
            Weeks
            <input type="number" min="0" v-model="admissionWeeks"
                   :style="highlightIfError('admissionWeeks')"
                   :disabled="isClose"/>
          </div>
          <div class="smd-input-label">
            Days
            <input type="number" min="0" v-model="admissionDays"
                   :style="highlightIfError('admissionDays')"
                   :disabled="isClose"/>
          </div>
        </div>
        <div class="form__row" v-else>
          <div class="form__row__item">
            <label>Estimated AOG:</label>
            <select v-model="admissionAOG"
                    :style="highlightIfError('admissionAOG')"
                    :disabled="isClose">
              <option v-for="mode in $options.aogRanges()"
                      :value="mode.id"
                      :key="mode.id">
                {{ mode.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="form__row" v-if="isPregnancy">
        <div class="form__row__item">
          <label>Type of Pregnancy</label>
          <select v-model="computedPregnancyType"
                  :style="highlightIfError('pregnancyType')"
                  :disabled="isClose">
            <option v-for="mode in $options.pregnancyTypes()"
                    :value="mode.id"
                    :key="mode.id">
              {{ mode.name }}
            </option>
          </select>
        </div>
        <!-- Removed in version 7
        <div v-if="isMultiple" class="form__row__item" :style="highlightIfError('isHeterotopic')">
          <label>Is this a Heterotopic Pregnancy?</label>
          <smd-segment type="pill"
                       v-model="isHeterotopic"
                       :items="$options.booleanChoices"
                       :disabled="isClose"/>
        </div>
        -->
        <div class="form__row__item" v-if="isAbortion">
          <label>Type of Abortion</label>
          <select v-model="abortionType"
                  :style="highlightIfError('abortionType')"
                  :disabled="isClose">
            <option v-for="mode in $options.abortionTypes()"
                    :value="mode.id"
                    :key="mode.id">
              {{ mode.name }}
            </option>
          </select>
        </div>
        <div class="form__row__item" v-if="isEctopic">
          <label>Type of Ecotopic Pregnancy</label>
          <select v-model="ectopicType"
                  :style="highlightIfError('ectopicType')"
                  :disabled="isClose">
            <option v-for="mode in $options.ectopicTypes()"
                    :value="mode.id"
                    :key="mode.id">
              {{ mode.name }}
            </option>
          </select>
        </div>
        <div class="form__row__item" v-if="isMolar">
          <label>Type of Molar Pregnancy</label>
          <select v-model="molarType"
                  :style="highlightIfError('molarType')"
                  :disabled="isClose">
            <option v-for="mode in $options.molarTypes()"
                    :value="mode.id"
                    :key="mode.id">
              {{ mode.name }}
            </option>
          </select>
        </div>

        <div class="smd-spacer"></div>
      </div>

      <div class="form__row--x">
        <label>Does patient have a history of PCOS?</label>
        <smd-segment type="pill"
                     v-model="pcosHistory"
                     :items="$options.booleanChoices"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="hasPCOSHistory">
        <label>Identify PCOS symptoms.</label>
        <select v-model="pcosSymptoms"
                :style="highlightIfError('pcosSymptoms')"
                :disabled="isClose">
          <option v-for="mode in $options.pcosSymptomTypes()"
                  :value="mode.id"
                  :key="mode.id">
            {{ mode.name }}
          </option>
        </select>
      </div>

      <div class="form__row--x" v-if="isPregnancy">
        <label>Complicated Pregnancy?</label>
        <smd-segment type="pill"
                     v-model="complicatedPregnancy"
                     :items="$options.booleanChoices"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="isComplicated">
        <label>Patient Returned to OR?</label>
        <smd-segment type="pill"
                     v-model="returnToOR"
                     :items="$options.booleanChoices"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="isOBCase" :style="highlightIfError('isPostpartum')">
        <label>Postpartum Admission?</label>
        <smd-segment type="pill"
                     v-model="isPostpartum"
                     :items="$options.booleanChoices"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="canDeliver" :style="highlightIfError('isDelivered')">
        <label>Has patient delivered?</label>
        <smd-segment type="pill"
                     v-model="isDelivered"
                     :items="$options.booleanChoices"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="hasDelivered" :style="highlightIfError('institutionalDelivery')">
        <label>Institutional Delivery?</label>
        <smd-segment type="pill"
                     v-model="institutionalDelivery"
                     :items="$options.institutionalDeliveryTypes()"
                     :disabled="isClose"/>
      </div>
    </div>
  </div>
</template>
<script>
import {
  getMapFn,
  REF_MATERNAL_FETAL_FORM_PREGNANCY_TYPE,
  REF_AOG_TYPES,
  REF_AOG_RANGES,
  REF_INSTITUTIONAL_DELIVERY_TYPES,
  REF_ABORTION_TYPE,
  REF_ECTOPIC_TYPE,
  REF_MOLAR_TYPE,
  REF_PCOSSYMPTOMS_TYPE
} from '../../../store/modules/referencedata2'
  import {createHelpers} from 'vuex-map-fields'
  import {
    GETTER_CASE_FORM_STATE_FIELD,
    SETTER_CASE_FORM_STATE_FIELD
  } from '../../../store/forms/case-form-state'
  import {newArrayWithObject, categorizeAOG} from "../../../utilities/utilityOperators";
  import withSectionValidation from '../../../mixins/with-section-validation';
  import withErrorValidation from '../../../mixins/with-error-validation-mixin';
  import validations from '../../../validations/admission-section-validations';

  const {mapFields} = createHelpers({
    getterType: GETTER_CASE_FORM_STATE_FIELD,
    mutationType: SETTER_CASE_FORM_STATE_FIELD
  });

  export default {
    mixins: [withSectionValidation, withErrorValidation],
    props: {
      birthdate : {type:String, required:true}
    },
    computed: {
      ...mapFields([
        'weight',
        'height',
        'bmi',
        'caseNum',
        'admissionAOGType',
        'admissionAOG',
        'admissionDate',
        'caseFormType',
        'isPostpartum',
        'isPregnancy',
        'isGyne',
        'admissionWeeks',
        'admissionDays',
        'pregnancyType',
        'isHeterotopic',
        'abortionType',
        'ectopicType',
        'molarType',
        'isDelivered',
        'infantsNumber',
        'neonatal',
        'age',
        'isClose',
        'institutionalDelivery',
        'complicatedPregnancy',
        'returnToOR',
        'pcosHistory',
        'pcosSymptoms'
      ]),
      hasPCOSHistory() {
        return this.pcosHistory;
      },
      canDeliver() {
        return this.isPregnancy && this.isPostpartum === false && (this.pregnancyType === 1 || this.pregnancyType === 2)
      },
      hasDelivered() {
        return this.isPregnancy && (this.pregnancyType === 1 || this.pregnancyType === 2) && this.isDelivered
      },
      isMultiple() {
        return this.isPregnancy &&  (this.pregnancyType === 2);
      },
      isAbortion() {
        return this.isPregnancy &&  (this.pregnancyType === 3);
      },
      isEctopic() {
        return this.isPregnancy &&  (this.pregnancyType === 4);
      },
      isMolar() {
        return this.isPregnancy &&  (this.pregnancyType === 5);
      },
      isOBCase() {
        return this.caseFormType === 1
      },
      isComplicated() {
        return this.complicatedPregnancy
      },
      computedPregnancyType: {
        get() {
          return this.pregnancyType
        },
        set(value) {
          this.pregnancyType = value;
          // Assume that 1 is for singleton pregnancy reference
          this.computedInfantsNumber = value === 1 ? 1 : 2
        }
      },
      computedInfantsNumber: {
        get() {
          return this.infantsNumber
        },
        set(newInfantCount) {
          this.infantsNumber = newInfantCount;
          if (this.neonatal.length < newInfantCount) {
            let extraElements = newArrayWithObject(this.$options.infantObjectTemplate)(newInfantCount - this.neonatal.length)
            this.neonatal = this.neonatal.concat(extraElements)
          }
        }
      },
      showExactAOG() {
        return this.admissionAOGType === 1
      },
      minDate() {
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2, 0, 1);
        return twoYearsAgo.toISOString().split('T')[0];
      },
      maxDate() {
        const today = new Date();
        return today.toISOString().split('T')[0];
      },
    },
    methods: {
      updateBMI() {
        if (!this.weight || !this.height) {
          this.bmi = null;
        } else {
          this.bmi = (this.weight / Math.pow(this.height/100, 2)).toFixed(2)
        }
      },
      getAge(date, fromDate) {
        let today = fromDate;
        let birthDate = date;
        let age = today.getFullYear() - birthDate.getFullYear();
        let m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate()))
        {
          age--;
        }
        return age;
      },
      initData() {
        if (this.admissionDate && !this.isClose) {
          this.age = this.getAge( new Date(this.birthdate), new Date(this.admissionDate));
        }
        if (this.isGyne) {
          this.caseFormType = 2
        }
      }
    },
    aogTypes: getMapFn(REF_AOG_TYPES),
    aogRanges: getMapFn(REF_AOG_RANGES),
    pregnancyTypes: getMapFn(REF_MATERNAL_FETAL_FORM_PREGNANCY_TYPE),
    abortionTypes: getMapFn(REF_ABORTION_TYPE),
    ectopicTypes: getMapFn(REF_ECTOPIC_TYPE),
    molarTypes: getMapFn(REF_MOLAR_TYPE),
    pcosSymptomTypes: getMapFn(REF_PCOSSYMPTOMS_TYPE),
    institutionalDeliveryTypes: getMapFn(REF_INSTITUTIONAL_DELIVERY_TYPES),
    validationSection: 'admission',
    booleanChoices: [
      {id: 1, name: 'YES', val: true},
      {id: 2, name: 'NO', val: false}
    ],
    caseTypeChoices: [
      {id: 1, name: 'OB', val: 1},
      {id: 2, name: 'GYNE', val: 2}
    ],
    infantObjectTemplate: {status: null, anomalies: null, pediatricAge: null, pediatricWeight: null, anomalyOthers: null, apgar1m: null, apgar5m: null, injury: null},
    created() {
      this.initData()
    },
    watch :{
      caseFormType(val) {
        if (val === 1) {
          this.isPregnancy = true;
          this.isGyne = false;
        } else if(val === 2) {
          this.isPregnancy = false;
          this.isDelivered = false;
          this.isGyne = true;
        }
      },
      isPostpartum(val) {
        if (val) {
          this.isDelivered = true
        }
      },
      isPregnancy(val) {
        if(!val) {
          this.isDelivered = false
        }
      },
      pregnancyType(nVal, oldVal) {
        if (!(nVal === 1 || nVal === 2)) {
          this.isDelivered = false
        }

        if (nVal !== oldVal) {
          if (nVal === 3) {
            this.molarType = null;
            this.ectopicType = null;
          } else if(nVal === 4) {
            this.abortionType = null;
            this.molarType = null;
          } else if (nVal === 5) {
            this.abortionType = null;
            this.ectopicType = null;
          } else {
            this.abortionType = null;
            this.ectopicType = null;
            this.molarType = null;
          }
        }
      },
      admissionDate(val) {
        if (val) {
          this.age = this.getAge( new Date(this.birthdate), new Date(val));
        }
      },
      admissionAOGType() {
        this.admissionAOG = categorizeAOG(this.admissionWeeks);
      },
      admissionWeeks(val) {
        this.admissionAOG = categorizeAOG(val);
      },
    },
    validations
  }
</script>


<style scoped>
  .form__row {
    display: flex;
    flex-wrap: wrap;
  }
  .error-messages {
    color: red;
    font-size: 0.8em;
  }
</style>
